import type { GridFontConfig, GridFontVariant, GridRendererConfig } from './config'
import { GRID_FONT_SOURCE_MAX_BYTES, type DecodedFontRecord } from './protocol'

const BUNDLED_FONT_URL = '/fonts/JetBrainsMono-Regular.ttf'
const VARIANTS: readonly GridFontVariant[] = ['regular', 'bold', 'italic', 'bold-italic']

interface InstalledFace {
  readonly variant: GridFontVariant
  readonly family: string
  readonly face: FontFace
  readonly sharedEmbedded: boolean
}

interface PendingConfiguration {
  readonly epoch: number
  readonly config: GridRendererConfig
  readonly expected: ReadonlyMap<GridFontVariant, GridFontConfig>
  readonly installed: Map<GridFontVariant, InstalledFace>
  readonly accepting: Set<GridFontVariant>
}

interface EmbeddedAsset {
  readonly bytes: Uint8Array
  readonly hash: string
}

interface EmbeddedFace extends InstalledFace {
  readonly sharedEmbedded: true
  readonly byteLength: number
  readonly hash: string
}

export class FontStoreError extends Error {
  constructor(message: string) {
    super(`font store: ${message}`)
    this.name = 'FontStoreError'
  }
}

function fail(message: string): never {
  throw new FontStoreError(message)
}

function bytesEqual(left: Uint8Array, right: Uint8Array): boolean {
  if (left.byteLength !== right.byteLength) return false
  for (let index = 0; index < left.byteLength; index++) {
    if (left[index] !== right[index]) return false
  }
  return true
}

function hashHex(bytes: Uint8Array): string {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
}

function arrayBufferView(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  if (bytes.buffer instanceof ArrayBuffer) {
    return new Uint8Array(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  }
  return Uint8Array.from(bytes)
}

async function sha256(bytes: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.digest('SHA-256', arrayBufferView(bytes)))
}

function descriptors(variant: GridFontVariant): FontFaceDescriptors {
  return {
    style: variant === 'italic' || variant === 'bold-italic' ? 'italic' : 'normal',
    weight: variant === 'bold' || variant === 'bold-italic' ? '700' : '400',
    display: 'block',
  }
}

function familyName(variant: GridFontVariant, hash: string): string {
  return `LeraGrid-${variant}-${hash.slice(0, 16)}`
}

function variantName(variant: 0 | 1 | 2 | 3): GridFontVariant {
  return VARIANTS[variant]
}

function canonicalNumber(value: number): string {
  return Object.is(value, -0) ? '-0' : value.toString()
}

function configurationFingerprint(config: GridRendererConfig): string {
  const color = ([red, green, blue]: readonly [number, number, number]): readonly string[] =>
    [canonicalNumber(red), canonicalNumber(green), canonicalNumber(blue)]
  return JSON.stringify([
    canonicalNumber(config.configGeneration),
    canonicalNumber(config.profileFontSize),
    canonicalNumber(config.fontSize),
    canonicalNumber(config.cssPxSize),
    canonicalNumber(config.lineHeight),
    canonicalNumber(config.cellWidth),
    canonicalNumber(config.cellHeight),
    canonicalNumber(config.baseline),
    config.palette.map(color),
    color(config.foreground),
    color(config.background),
    color(config.cursor),
    config.cursorStyle,
    config.cursorBlink,
    config.fonts.map((font) => [
      font.variant, font.hash, canonicalNumber(font.byteLength), font.embedded,
    ]),
  ])
}

export class FontStore {
  #epoch = 0
  #latestGeneration = 0
  #latestFingerprint: string | null = null
  #latestState: 'none' | 'pending' | 'failed' | 'committed' = 'none'
  #disposed = false
  #pending: PendingConfiguration | null = null
  #activeGeneration = 0
  #active = new Map<GridFontVariant, InstalledFace>()
  #embeddedAsset: Promise<EmbeddedAsset> | null = null
  #embeddedFace: EmbeddedFace | null = null

  async configure(config: GridRendererConfig): Promise<void> {
    if (this.#disposed) fail('store is disposed')
    if (config.configGeneration < this.#latestGeneration) fail('stale configuration generation')
    if (config.configGeneration === this.#latestGeneration && this.#latestState !== 'failed') {
      fail('duplicate configuration generation')
    }
    const fingerprint = configurationFingerprint(config)
    if (config.configGeneration === this.#latestGeneration && fingerprint !== this.#latestFingerprint) {
      fail('configuration retry does not match the failed generation')
    }
    if (this.#pending !== null) this.#abortPending(this.#pending, false)
    this.#latestGeneration = config.configGeneration
    this.#latestFingerprint = fingerprint
    this.#latestState = 'pending'
    const epoch = ++this.#epoch
    this.#activeGeneration = 0
    const expected = new Map(config.fonts.map((font) => [font.variant, font]))
    const pending: PendingConfiguration = {
      epoch, config, expected, installed: new Map(), accepting: new Set(),
    }
    this.#pending = pending

    try {
      for (const font of config.fonts) {
        if (!font.embedded) continue
        if (font.variant !== 'regular') fail('only the regular font may be bundled')
        const installed = await this.#loadEmbedded(font, epoch)
        this.#requireCurrent(pending)
        pending.installed.set(font.variant, installed)
      }
      this.#commitIfComplete(pending)
    } catch (error) {
      if (this.#pending === pending) this.#abortPending(pending, true)
      throw error
    }
  }

  async accept(record: DecodedFontRecord): Promise<void> {
    if (this.#disposed) fail('store is disposed')
    const pending = this.#pending
    if (pending === null) {
      if (record.configGeneration === this.#activeGeneration &&
          this.#active.has(variantName(record.variant))) fail('duplicate font record')
      fail('no configuration is awaiting fonts')
    }
    if (record.configGeneration !== pending.config.configGeneration) fail('font generation does not match configuration')
    const variant = variantName(record.variant)
    const expected = pending.expected.get(variant)
    if (expected === undefined || expected.embedded) fail('font variant is not expected from the stream')
    if (pending.installed.has(variant) || pending.accepting.has(variant)) fail('duplicate font record')
    if (record.format !== 1) {
      this.#abortPending(pending, true)
      fail('font record format is invalid')
    }
    const sourceBytes = record.bytes
    if (!(sourceBytes instanceof Uint8Array)) {
      this.#abortPending(pending, true)
      fail('font record byte storage is invalid')
    }
    if (sourceBytes.byteLength > GRID_FONT_SOURCE_MAX_BYTES) {
      this.#abortPending(pending, true)
      fail('font record exceeds the bounded logical source size')
    }
    if (sourceBytes.byteLength !== expected.byteLength) {
      this.#abortPending(pending, true)
      fail('font record length does not match configuration')
    }
    const expectedHash = Uint8Array.from(expected.hash.match(/../gu)!.map((pair) => Number.parseInt(pair, 16)))
    if (!bytesEqual(record.sha256, expectedHash)) {
      this.#abortPending(pending, true)
      fail('font record hash does not match configuration')
    }

    pending.accepting.add(variant)
    let installed: InstalledFace | null = null
    try {
      const ownedBytes = new Uint8Array(sourceBytes)
      const actualHash = await sha256(ownedBytes)
      this.#requireCurrent(pending)
      if (!bytesEqual(actualHash, expectedHash)) fail('font content hash does not match its record')
      installed = await this.#installCustom(variant, expected.hash, ownedBytes, pending)
      this.#requireCurrent(pending)
      pending.installed.set(variant, installed)
      pending.accepting.delete(variant)
      this.#commitIfComplete(pending)
    } catch (error) {
      if (installed !== null) document.fonts.delete(installed.face)
      if (this.#pending === pending) this.#abortPending(pending, true)
      throw error
    }
  }

  isReady(configGeneration: number): boolean {
    return !this.#disposed && this.#activeGeneration === configGeneration && configGeneration !== 0
  }

  familyFor(variant: 0 | 1 | 2 | 3, configGeneration: number): string {
    if (!this.isReady(configGeneration)) fail('configuration fonts are not ready')
    const installed = this.#active.get(variantName(variant))
    if (installed === undefined) fail('font variant is unavailable')
    return installed.family
  }

  availableVariants(configGeneration: number): ReadonlySet<GridFontVariant> {
    if (!this.isReady(configGeneration)) return new Set()
    return new Set(this.#active.keys())
  }

  dispose(): void {
    if (this.#disposed) return
    this.#disposed = true
    ++this.#epoch
    if (this.#pending !== null) this.#abortPending(this.#pending, false)
    this.#deleteFaces(Array.from(this.#active.values()).filter((face) => !face.sharedEmbedded))
    this.#active.clear()
    if (this.#embeddedFace !== null) {
      document.fonts.delete(this.#embeddedFace.face)
      this.#embeddedFace = null
    }
    this.#activeGeneration = 0
  }

  async #loadEmbedded(expected: GridFontConfig, epoch: number): Promise<EmbeddedFace> {
    const asset = await this.#bundledAsset()
    if (epoch !== this.#epoch) fail('stale bundled font load')
    if (asset.bytes.byteLength !== expected.byteLength || asset.hash !== expected.hash) {
      fail('bundled font identity does not match the declared record')
    }
    if (this.#embeddedFace !== null) {
      if (this.#embeddedFace.hash !== expected.hash || this.#embeddedFace.byteLength !== expected.byteLength) {
        fail('bundled font identity changed')
      }
      return this.#embeddedFace
    }
    const family = familyName('regular', expected.hash)
    const face = new FontFace(family, arrayBufferView(asset.bytes), descriptors('regular'))
    await face.load()
    if (epoch !== this.#epoch) fail('stale bundled font load')
    document.fonts.add(face)
    this.#embeddedFace = {
      variant: 'regular', family, face, sharedEmbedded: true,
      byteLength: expected.byteLength, hash: expected.hash,
    }
    return this.#embeddedFace
  }

  #bundledAsset(): Promise<EmbeddedAsset> {
    if (this.#embeddedAsset !== null) return this.#embeddedAsset
    const loading = (async () => {
      const response = await fetch(BUNDLED_FONT_URL, { credentials: 'same-origin' })
      if (!response.ok) fail('bundled font could not be loaded')
      const bytes = new Uint8Array(await response.arrayBuffer())
      if (bytes.byteLength === 0 || bytes.byteLength > GRID_FONT_SOURCE_MAX_BYTES) {
        fail('bundled font has an invalid bounded length')
      }
      return { bytes, hash: hashHex(await sha256(bytes)) }
    })()
    this.#embeddedAsset = loading
    void loading.then(undefined, () => {
      if (this.#embeddedAsset === loading) this.#embeddedAsset = null
    })
    return loading
  }

  async #installCustom(variant: GridFontVariant, hash: string, bytes: Uint8Array,
                       pending: PendingConfiguration): Promise<InstalledFace> {
    const url = URL.createObjectURL(new Blob([arrayBufferView(bytes)], { type: 'font/ttf' }))
    try {
      const family = familyName(variant, hash)
      const face = new FontFace(family, `url("${url}") format("truetype")`, descriptors(variant))
      await face.load()
      this.#requireCurrent(pending)
      document.fonts.add(face)
      return { variant, family, face, sharedEmbedded: false }
    } finally {
      URL.revokeObjectURL(url)
    }
  }

  #requireCurrent(pending: PendingConfiguration): void {
    if (this.#disposed || this.#pending !== pending || pending.epoch !== this.#epoch) fail('stale font transaction')
  }

  #commitIfComplete(pending: PendingConfiguration): void {
    this.#requireCurrent(pending)
    if (pending.accepting.size !== 0 || pending.installed.size !== pending.expected.size) return
    const previous = this.#active
    this.#active = new Map(pending.installed)
    this.#activeGeneration = pending.config.configGeneration
    this.#latestState = 'committed'
    this.#pending = null
    this.#deleteFaces(Array.from(previous.values()).filter((installed) =>
      !installed.sharedEmbedded && !Array.from(this.#active.values()).some((current) => current.face === installed.face)))
  }

  #abortPending(pending: PendingConfiguration, failed: boolean): void {
    if (this.#pending !== pending) return
    this.#pending = null
    if (pending.config.configGeneration === this.#latestGeneration && this.#latestState === 'pending') {
      this.#latestState = failed ? 'failed' : 'none'
    }
    pending.accepting.clear()
    this.#deleteFaces(Array.from(pending.installed.values()).filter((face) => !face.sharedEmbedded))
    pending.installed.clear()
  }

  #deleteFaces(faces: Iterable<InstalledFace>): void {
    const seen = new Set<FontFace>()
    for (const installed of faces) {
      if (seen.has(installed.face)) continue
      seen.add(installed.face)
      document.fonts.delete(installed.face)
    }
  }
}

export { BUNDLED_FONT_URL }
